import { useEffect, useMemo, useRef, useState } from "react";
import { getSocket } from "../lib/socket";
import { trpc } from "../lib/trpc";

type CancelVoteReason = "server_issue" | "game_bug" | "connection_issue" | "unfair_start" | "other";
type CancelVoteStatus = "pending" | "passed" | "failed" | "expired";
type CancelVoteDisabledReason =
  | "disabled"
  | "not_in_progress"
  | "too_early"
  | "active_vote"
  | "max_votes_reached"
  | null;

type CancelVoteView = {
  voteId: string;
  initiatorId: string;
  status: CancelVoteStatus;
  reason: CancelVoteReason;
  reasonOtherText: string | null;
  threshold: number;
  expiresAt: string;
  resolvedAt: string | null;
  yesCount: number;
  noCount: number;
  totalPlayers: number;
};

type UseCancelVoteOptions = {
  matchId: string;
  currentUserId: string;
  totalPlayers: number;
  enabled: boolean;
};

type StartVoteInput = {
  reason: CancelVoteReason;
  reasonOtherText?: string;
};

type SocketVoteStartedEvent = {
  voteId: string;
  initiatorId: string;
  expiresAt: string;
  threshold: number;
  reason: CancelVoteReason;
  reasonOtherText: string | null;
};

type SocketVoteUpdatedEvent = {
  voteId: string;
  yesCount: number;
  noCount: number;
  totalPlayers: number;
  threshold: number;
  reason: CancelVoteReason;
  reasonOtherText: string | null;
};

type SocketVoteResolvedEvent = {
  voteId: string;
  status: CancelVoteStatus;
  reason: CancelVoteReason;
  reasonOtherText: string | null;
};

function toIsoString(value: Date | string | null): string | null {
  if (!value) return null;
  return value instanceof Date ? value.toISOString() : value;
}

export function useCancelVote({
  matchId,
  currentUserId,
  totalPlayers,
  enabled,
}: UseCancelVoteOptions) {
  const [activeVote, setActiveVote] = useState<CancelVoteView | null>(null);
  const [myVote, setMyVote] = useState<boolean | null>(null);
  const [nowMs, setNowMs] = useState(Date.now());
  const dismissTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const utils = trpc.useUtils();
  const invalidateCancelVoteState = async () =>
    utils.match.getCancelVoteState.invalidate({ matchId });

  const stateQuery = trpc.match.getCancelVoteState.useQuery(
    { matchId },
    {
      enabled,
      staleTime: 5_000,
      refetchInterval: 5_000,
    },
  );

  const initiateVoteMutation = trpc.match.initiateCancelVote.useMutation();
  const castVoteMutation = trpc.match.castCancelVote.useMutation();

  useEffect(() => {
    return () => {
      if (dismissTimeoutRef.current) {
        clearTimeout(dismissTimeoutRef.current);
        dismissTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const state = stateQuery.data;
    if (!state) {
      return;
    }

    setMyVote(state.myVote);

    if (state.pendingVote) {
      setActiveVote({
        voteId: state.pendingVote.voteId,
        initiatorId: state.pendingVote.initiatorId,
        status: state.pendingVote.status,
        reason: state.pendingVote.reason,
        reasonOtherText: state.pendingVote.reasonOtherText,
        threshold: state.pendingVote.threshold,
        expiresAt: toIsoString(state.pendingVote.expiresAt) ?? new Date().toISOString(),
        resolvedAt: toIsoString(state.pendingVote.resolvedAt),
        yesCount: state.pendingVote.yesCount,
        noCount: state.pendingVote.noCount,
        totalPlayers: state.pendingVote.totalPlayers,
      });
      return;
    }

    setActiveVote((prev) => (prev?.status === "pending" ? null : prev));
  }, [stateQuery.data]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const socket = getSocket();

    const handleStarted = (event: SocketVoteStartedEvent) => {
      if (dismissTimeoutRef.current) {
        clearTimeout(dismissTimeoutRef.current);
        dismissTimeoutRef.current = null;
      }

      setActiveVote({
        voteId: event.voteId,
        initiatorId: event.initiatorId,
        status: "pending",
        reason: event.reason,
        reasonOtherText: event.reasonOtherText,
        threshold: event.threshold,
        expiresAt: event.expiresAt,
        resolvedAt: null,
        yesCount: 1,
        noCount: 0,
        totalPlayers,
      });
      setMyVote(event.initiatorId === currentUserId ? true : null);
      void invalidateCancelVoteState();
    };

    const handleUpdated = (event: SocketVoteUpdatedEvent) => {
      setActiveVote((prev) => {
        if (!prev || prev.voteId !== event.voteId) {
          return prev;
        }

        return {
          ...prev,
          threshold: event.threshold,
          reason: event.reason,
          reasonOtherText: event.reasonOtherText,
          yesCount: event.yesCount,
          noCount: event.noCount,
          totalPlayers: event.totalPlayers,
        };
      });
      void invalidateCancelVoteState();
    };

    const handleResolved = (event: SocketVoteResolvedEvent) => {
      setActiveVote((prev) => {
        if (!prev || prev.voteId !== event.voteId) {
          return prev;
        }

        return {
          ...prev,
          status: event.status,
          reason: event.reason,
          reasonOtherText: event.reasonOtherText,
          resolvedAt: new Date().toISOString(),
        };
      });
      void invalidateCancelVoteState();
    };

    socket.on("cancel_vote_started", handleStarted);
    socket.on("cancel_vote_updated", handleUpdated);
    socket.on("cancel_vote_resolved", handleResolved);

    return () => {
      socket.off("cancel_vote_started", handleStarted);
      socket.off("cancel_vote_updated", handleUpdated);
      socket.off("cancel_vote_resolved", handleResolved);
    };
  }, [currentUserId, enabled, invalidateCancelVoteState, matchId, totalPlayers]);

  useEffect(() => {
    if (activeVote?.status !== "pending") {
      return;
    }

    const id = setInterval(() => {
      setNowMs(Date.now());
    }, 1000);

    return () => clearInterval(id);
  }, [activeVote?.status]);

  useEffect(() => {
    if (!activeVote || activeVote.status === "pending" || activeVote.status === "passed") {
      return;
    }

    if (dismissTimeoutRef.current) {
      clearTimeout(dismissTimeoutRef.current);
    }
    dismissTimeoutRef.current = setTimeout(() => {
      setActiveVote((prev) => (prev?.voteId === activeVote.voteId ? null : prev));
      setMyVote(null);
      dismissTimeoutRef.current = null;
    }, 5_000);
  }, [activeVote]);

  const secondsRemaining = useMemo(() => {
    if (!activeVote || activeVote.status !== "pending") {
      return null;
    }

    const expiresAtMs = new Date(activeVote.expiresAt).getTime();
    const remaining = Math.ceil((expiresAtMs - nowMs) / 1000);
    return Math.max(0, remaining);
  }, [activeVote, nowMs]);

  const requiredYesVotes = useMemo(() => {
    const threshold = activeVote?.threshold ?? stateQuery.data?.threshold ?? 0.75;
    const players = activeVote?.totalPlayers ?? totalPlayers;
    return Math.ceil(threshold * players);
  }, [activeVote, stateQuery.data?.threshold, totalPlayers]);

  const startVote = async ({ reason, reasonOtherText }: StartVoteInput) => {
    const vote = await initiateVoteMutation.mutateAsync({
      matchId,
      reason,
      reasonOtherText,
    });

    setActiveVote({
      voteId: vote.voteId,
      initiatorId: vote.initiatorId,
      status: vote.status,
      reason: vote.reason,
      reasonOtherText: vote.reasonOtherText,
      threshold: vote.threshold,
      expiresAt: toIsoString(vote.expiresAt) ?? new Date().toISOString(),
      resolvedAt: toIsoString(vote.resolvedAt),
      yesCount: vote.yesCount,
      noCount: vote.noCount,
      totalPlayers: vote.totalPlayers,
    });
    setMyVote(true);
    await invalidateCancelVoteState();
  };

  const castVote = async (vote: boolean) => {
    if (!activeVote?.voteId) {
      throw new Error("No active cancel vote to cast on");
    }

    const next = await castVoteMutation.mutateAsync({
      matchId,
      voteId: activeVote.voteId,
      vote,
    });

    setActiveVote((prev) => ({
      voteId: next.voteId,
      initiatorId: next.initiatorId,
      status: next.status,
      reason: next.reason,
      reasonOtherText: next.reasonOtherText,
      threshold: next.threshold,
      expiresAt: toIsoString(next.expiresAt) ?? prev?.expiresAt ?? new Date().toISOString(),
      resolvedAt: toIsoString(next.resolvedAt),
      yesCount: next.yesCount,
      noCount: next.noCount,
      totalPlayers: next.totalPlayers,
    }));
    setMyVote(vote);
    await invalidateCancelVoteState();
  };

  const disabledMessage = stateQuery.data?.disabledMessage ?? null;
  const disabledReason = stateQuery.data?.disabledReason ?? null;
  const canInitiate = stateQuery.data?.canInitiate ?? false;
  const threshold = stateQuery.data?.threshold ?? 0.75;
  const votesRemaining = stateQuery.data?.votesRemaining ?? 0;
  const availableInSeconds = stateQuery.data?.availableInSeconds ?? 0;

  return {
    activeVote,
    myVote,
    secondsRemaining,
    requiredYesVotes,
    threshold,
    canInitiate,
    disabledReason: disabledReason as CancelVoteDisabledReason,
    disabledMessage,
    votesRemaining,
    availableInSeconds,
    isStartingVote: initiateVoteMutation.isPending,
    isCastingVote: castVoteMutation.isPending,
    startVote,
    castVote,
  };
}
